/**
 * ExtendJobModal — intentional, accountable extension of an installed job's
 * shift count. Same crew, same start, +N trailing shifts.
 *
 * Operational philosophy:
 *   • Field grace, not silent override. Every extension is logged + traceable.
 *   • Soft thresholds, not approvals (SMB-friendly). Friction escalates with
 *     intent: free → warning → typed CONFIRM. Never blocks.
 *   • Show consequences before confirm — the parent grid surfaces a live
 *     preview span via `onPreviewShiftsChange`.
 *
 * Side effects on confirm:
 *   1. Optimistic Job.scheduledShifts update with 8s undo.
 *   2. Activity timeline entry with reason.
 *   3. Issue ("Scheduling Issue", titled "Field Extension: …") unless reason
 *      = "Customer change" — feeds Service Call Prevention scoring.
 *
 * Reuses: useAppData (jobs setter), capacity engine spans, sonner toast.
 */
import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useAppData } from "@/data/AppDataContext";
import { toast } from "sonner";
import type { Job, Issue } from "@/data/types";
import type { CapacityRow } from "@/data/scheduling/capacityEngine";
import { Minus, Plus, AlertTriangle, Info } from "lucide-react";
import { format, parseISO, addDays } from "date-fns";
import { cn } from "@/lib/utils";

const REASON_OPTIONS = [
  "Material delay",
  "Permit delay",
  "Hidden conditions",
  "Customer change",
  "Crew issue",
  "Weather",
  "Other",
] as const;
type Reason = typeof REASON_OPTIONS[number];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
  /** Crew row (this week) — used for remaining-capacity bound + utilization preview. */
  crewRow: CapacityRow | null;
  /** Lifts the user's pending shift count up to the grid for live preview highlighting. */
  onPreviewShiftsChange?: (shifts: number | null) => void;
}

export function ExtendJobModal({ open, onOpenChange, job, crewRow, onPreviewShiftsChange }: Props) {
  const { setJobs, setIssues } = useAppData();

  const originalShifts = Math.max(1, job.scheduledShifts ?? 1);
  // Hard cap stays at 5 shifts (operational sanity). We intentionally do NOT
  // bound by *this week's* remaining crew capacity — real installs cross week
  // boundaries (e.g. Thu start, +3 = Mon next week). Capacity overrun in the
  // current week still surfaces below as a warning ("May delay this crew's
  // next job"), so the operator sees the consequence without being locked out.
  const maxShifts = 5;
  // Whether the requested span runs past the current week's remaining capacity.
  // Surfaces as a soft notice — never blocks.
  const remainingWeekCapacity = crewRow
    ? Math.max(0, crewRow.capacityDays - crewRow.bookedDays) + originalShifts
    : maxShifts;

  const [shifts, setShifts] = useState(originalShifts);
  const [reason, setReason] = useState<Reason | "">("");
  const [otherText, setOtherText] = useState("");
  const [confirmText, setConfirmText] = useState("");

  // Reset on open / job change
  useEffect(() => {
    if (open) {
      setShifts(originalShifts);
      setReason("");
      setOtherText("");
      setConfirmText("");
    }
  }, [open, originalShifts]);

  // Lift preview to parent grid
  useEffect(() => {
    if (!open) {
      onPreviewShiftsChange?.(null);
      return;
    }
    onPreviewShiftsChange?.(shifts);
    return () => onPreviewShiftsChange?.(null);
  }, [open, shifts, onPreviewShiftsChange]);

  const delta = shifts - originalShifts;
  const isReduction = delta < 0;
  const absDelta = Math.abs(delta);

  // ── Threshold logic ──────────────────────────────────────────────────
  // +0..1 shift  → no friction
  // +2 shifts    → warning banner
  // > original × 1.5 → require typed CONFIRM
  const requiresConfirm = !isReduction && shifts > Math.ceil(originalShifts * 1.5) && delta >= 2;
  const showSoftWarning = !isReduction && delta >= 2 && !requiresConfirm;

  // ── End-day label (skip weekends) ────────────────────────────────────
  const newEndLabel = useMemo(() => {
    if (!job.scheduledInstallDate) return "—";
    let cursor = parseISO(job.scheduledInstallDate);
    for (let i = 1; i < shifts; i++) {
      cursor = addDays(cursor, 1);
      while (cursor.getDay() === 0 || cursor.getDay() === 6) cursor = addDays(cursor, 1);
    }
    return format(cursor, "EEE MMM d");
  }, [job.scheduledInstallDate, shifts]);

  // ── Capacity impact ──────────────────────────────────────────────────
  const capacityPct = crewRow
    ? Math.round(((crewRow.bookedDays + delta) / crewRow.capacityDays) * 100)
    : null;
  const overCapacity = capacityPct !== null && capacityPct > 100;

  // ── Validation ───────────────────────────────────────────────────────
  const reasonValid = reason !== "" && (reason !== "Other" || otherText.trim().length > 0);
  const confirmValid = !requiresConfirm || confirmText.trim().toUpperCase() === "CONFIRM";
  const canSubmit = delta !== 0 && reasonValid && confirmValid;

  // ── Submit ───────────────────────────────────────────────────────────
  const handleSubmit = () => {
    if (!canSubmit) return;
    const reasonLabel = reason === "Other" ? `Other: ${otherText.trim()}` : reason;
    const previousShifts = originalShifts;
    const nextShifts = shifts;

    // Optimistic update
    setJobs((prev) =>
      prev.map((j) =>
        j.id !== job.id
          ? j
          : {
              ...j,
              scheduledShifts: nextShifts,
              activityHistory: [
                ...j.activityHistory,
                {
                  id: `ext-${Date.now()}`,
                  date: new Date().toISOString(),
                  action: `Job extended ${previousShifts} → ${nextShifts} shifts (${reasonLabel})`,
                  user: "Dispatch",
                  noteType: "ops" as const,
                },
              ],
            },
      ),
    );

    // Issue logging — skip for "Customer change" (not an operational fault).
    let createdIssueId: string | null = null;
    if (reason !== "Customer change" && delta > 0) {
      createdIssueId = `iss-ext-${Date.now()}`;
      const newIssue: Issue = {
        id: createdIssueId,
        jobId: job.id,
        issueType: "Scheduling Issue",
        severity: delta >= 2 ? "Medium" : "Low",
        status: "Open",
        description: `Field Extension: ${reasonLabel}. Install extended from ${previousShifts} to ${nextShifts} shifts.`,
        photo: "",
        owner: "Dispatch",
        dateCreated: new Date().toISOString(),
        source: "Admin",
        jobStageAtCreation: job.currentStage,
      };
      setIssues((prev) => [...prev, newIssue]);
    }

    onOpenChange(false);
    onPreviewShiftsChange?.(null);

    // 8s undo toast
    const summary = delta > 0
      ? `Extended +${absDelta} shift${absDelta === 1 ? "" : "s"} · ends ${newEndLabel}`
      : `Reduced −${absDelta} shift${absDelta === 1 ? "" : "s"}`;
    toast.success(summary, {
      duration: 8000,
      action: {
        label: "Undo",
        onClick: () => {
          setJobs((prev) =>
            prev.map((j) =>
              j.id !== job.id
                ? j
                : {
                    ...j,
                    scheduledShifts: previousShifts,
                    activityHistory: [
                      ...j.activityHistory,
                      {
                        id: `ext-undo-${Date.now()}`,
                        date: new Date().toISOString(),
                        action: `Extension undone — restored to ${previousShifts} shifts`,
                        user: "Dispatch",
                        noteType: "ops" as const,
                      },
                    ],
                  },
            ),
          );
          if (createdIssueId) {
            setIssues((prev) => prev.filter((i) => i.id !== createdIssueId));
          }
        },
      },
    });
  };

  const dec = () => setShifts((s) => Math.max(1, s - 1));
  const inc = () => setShifts((s) => Math.min(maxShifts, s + 1));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Extend installation</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Job context */}
          <div className="text-xs text-muted-foreground">
            <span className="text-foreground font-medium">{job.customerName}</span>
            {" · "}{job.projectType}
          </div>

          {/* Stepper */}
          <div className="border border-border rounded-md p-4 bg-muted/10">
            <p className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">
              Shift count
            </p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] text-muted-foreground">Original</p>
                <p className="text-sm font-medium tabular-nums">{originalShifts} shift{originalShifts === 1 ? "" : "s"}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-9 w-9"
                  onClick={dec}
                  disabled={shifts <= 1}
                  aria-label="Decrease shifts"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <div className="w-14 text-center">
                  <p className="text-[11px] text-muted-foreground">New</p>
                  <p className="text-xl font-semibold tabular-nums">{shifts}</p>
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-9 w-9"
                  onClick={inc}
                  disabled={shifts >= maxShifts}
                  aria-label="Increase shifts"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">
              New span: <span className="text-foreground font-medium">
                {format(parseISO(job.scheduledInstallDate), "EEE MMM d")} → {newEndLabel}
              </span>
            </p>
            {shifts > remainingWeekCapacity && (
              <p className="text-[11px] text-warning mt-1">
                Extends past {crewRow?.crewName ?? "this crew"}'s remaining capacity this week — span continues into next week.
              </p>
            )}
          </div>

          {/* Impact signals */}
          {delta !== 0 && (
            <div className="border border-border rounded-md p-3 space-y-1.5 bg-card">
              <div className="flex items-start gap-2 text-xs">
                <Info className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                <div className="space-y-1">
                  {capacityPct !== null && (
                    <p className="text-foreground">
                      Crew weekly utilization:{" "}
                      <span className={cn(
                        "font-medium tabular-nums",
                        overCapacity ? "text-destructive" : capacityPct >= 80 ? "text-warning" : "text-muted-foreground",
                      )}>
                        {capacityPct}%
                      </span>
                      {delta > 0 && <span className="text-muted-foreground"> ({delta > 0 ? "+" : ""}{delta} shift{absDelta === 1 ? "" : "s"})</span>}
                    </p>
                  )}
                  {overCapacity && (
                    <p className="text-destructive">May delay this crew's next job.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Soft warning */}
          {showSoftWarning && (
            <div className="border border-warning/40 bg-warning/5 rounded-md p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <div className="text-xs">
                <p className="font-medium text-warning">Above normal extension range</p>
                <p className="text-muted-foreground mt-0.5">
                  Most extensions are +1 shift. This will be flagged on the crew's extension-rate KPI.
                </p>
              </div>
            </div>
          )}

          {/* Reason — required */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Reason <span className="text-destructive">*</span>
            </label>
            <Select value={reason} onValueChange={(v) => setReason(v as Reason)}>
              <SelectTrigger className="h-9 text-xs">
                <SelectValue placeholder="Why is this being extended?" />
              </SelectTrigger>
              <SelectContent>
                {REASON_OPTIONS.map((r) => (
                  <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {reason === "Other" && (
              <Textarea
                placeholder="Describe the reason…"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                className="text-xs min-h-[60px]"
              />
            )}
            {reason && reason !== "Customer change" && delta > 0 && (
              <p className="text-[11px] text-muted-foreground">
                A "Field Extension" issue will be created and linked to this job.
              </p>
            )}
          </div>

          {/* Typed CONFIRM */}
          {requiresConfirm && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                This is well above the original allotment. Type <span className="font-mono text-destructive">CONFIRM</span> to proceed.
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="CONFIRM"
                className="h-9 text-xs font-mono"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {delta > 0 ? `Extend by ${absDelta} shift${absDelta === 1 ? "" : "s"}` :
             delta < 0 ? `Reduce by ${absDelta} shift${absDelta === 1 ? "" : "s"}` :
             "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
